import React, { useEffect } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Easing, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { ThemedText } from '../themed-text';
import { cn } from '@/utils/cn';
import { AnimatedView } from './animated';

export interface StepConfig {
  title: string;
  icon?: React.ComponentProps<typeof Ionicons>['name'];
}

export interface StepIndicatorProps {
  steps: StepConfig[];
  /** Passo atual, começando em 1 */
  currentStep: number;
  className?: string;
}

const BAR_TRANSITION = { duration: 280, easing: Easing.out(Easing.cubic) };

/**
 * Indicador de passos para fluxos guiados: "Passo X de Y", nome do passo
 * atual e barra de progresso animada. Pensado para leitura rápida.
 */
export function StepIndicator({ steps, currentStep, className }: StepIndicatorProps) {
  const progress = useSharedValue(currentStep / steps.length);

  useEffect(() => {
    progress.value = withTiming(currentStep / steps.length, BAR_TRANSITION);
  }, [currentStep, steps.length, progress]);

  const barStyle = useAnimatedStyle(() => ({
    width: `${progress.value * 100}%`,
  }));

  const step = steps[currentStep - 1];
  const nextStep = steps[currentStep];

  return (
    <View className={cn('gap-2.5', className)}>
      <View className="flex-row items-end justify-between">
        <View className="gap-0.5">
          <ThemedText className="font-semibold text-xs uppercase tracking-wide text-primary">
            Passo {currentStep} de {steps.length}
          </ThemedText>
          <View className="flex-row items-center gap-2">
            {step?.icon ? <Ionicons className="text-neutral-900" name={step.icon} size={18} /> : null}
            <ThemedText className="font-bold text-lg text-neutral-900">{step?.title}</ThemedText>
          </View>
        </View>
        {nextStep ? (
          <ThemedText className="text-xs text-neutral-400">Próximo: {nextStep.title}</ThemedText>
        ) : null}
      </View>
      <View className="h-2 overflow-hidden rounded-full bg-neutral-200/70">
        <AnimatedView
          style={[{ height: '100%', borderRadius: 9999, backgroundColor: '#FD5001' }, barStyle]}
        />
      </View>
    </View>
  );
}
